<template>
    <v-container fluid class="elib-page e-libaray pa-4 pa-sm-6">
        <!-- toolbar -->
        <v-card class="elib-shell" flat :loading="loading">
            <div class="elib-toolbar">
                <v-btn depressed small color="primary" class="elib-btn" @click="addNewCategory">
                    <v-icon small class="me-1">mdi-folder-plus-outline</v-icon>
                    {{ $t('AddFolder') }}
                </v-btn>
                <v-btn depressed small color="secondary" class="elib-btn white--text" @click="addAttachmentDialog = true">
                    <v-icon small class="me-1">mdi-cloud-upload-outline</v-icon>
                    {{ $t('AddAttachment') }}
                </v-btn>

                <span class="elib-toolbar__divider" />

                <v-btn v-if="selectedCategoryId" depressed small class="elib-btn elib-btn--muted" @click="editCategory()">
                    <v-icon small class="me-1">mdi-pencil-outline</v-icon>
                    {{ $t('EditFolder') }}
                </v-btn>
                <v-btn v-if="selectedCategoryId" depressed small class="elib-btn elib-btn--muted" @click="categoryPermissionDialog = true">
                    <v-icon small class="me-1">mdi-shield-account-outline</v-icon>
                    {{ $t('FolderPermissions') }}
                </v-btn>
                <v-btn v-if="selectedCategoryId && canBeDeleted" depressed small color="error" class="elib-btn" @click="deleteCategory(selectedCategoryId)">
                    <v-icon small class="me-1">mdi-trash-can-outline</v-icon>
                    {{ $t('DeleteFolder') }}
                </v-btn>
            </div>

            <div class="pa-4 pa-sm-5">
                <v-row>
                    <!-- folder tree -->
                    <v-col cols="12" md="4" lg="3">
                        <section class="elib-panel e-library-tree-panel">
                            <div class="elib-panel__head">
                                <span class="elib-panel__head-icon">
                                    <v-icon small>mdi-folder-multiple-outline</v-icon>
                                </span>
                                <h2 class="elib-panel__title">{{ tr('Folders', 'Folders', 'المجلدات') }}</h2>
                            </div>

                            <div class="elib-panel__toolbar e-library-tree-search">
                                <v-text-field v-model="search"
                                              class="elib-search"
                                              dense
                                              outlined
                                              rounded
                                              hide-details
                                              clearable
                                              prepend-inner-icon="mdi-magnify"
                                              clear-icon="mdi-close-circle-outline"
                                              :label="$t('Search')"
                                              @input="handleSearch" />
                            </div>

                            <div class="e-library-tree-scroll">
                                <v-treeview ref="tree" class="elib-tree" :items="treecategories" :search="search" :filter="filter" activatable item-key="id" :open.sync="open">
                                    <template v-slot:label="{ item, leaf, open: isOpen }">
                                        <div class="elib-node"
                                             :class="{ 'elib-node--branch': !leaf, 'elib-node--selected': item.id === selectedCategoryId }"
                                             role="button"
                                             tabindex="0"
                                             :aria-current="item.id === selectedCategoryId ? 'true' : null"
                                             @click="show(item)"
                                             @keydown.enter.prevent="show(item)"
                                             @keydown.space.prevent="show(item)">
                                            <span class="elib-node__icon">
                                                <v-icon small>{{ leaf ? 'mdi-folder-outline' : (isOpen ? 'mdi-folder-open-outline' : 'mdi-folder-outline') }}</v-icon>
                                            </span>
                                            <span class="elib-node__text">{{ item.name }}</span>
                                        </div>
                                    </template>
                                </v-treeview>
                            </div>
                        </section>
                    </v-col>

                    <!-- attachments -->
                    <v-col cols="12" md="8" lg="9">
                        <section class="elib-panel">
                            <div class="elib-panel__head">
                                <span class="elib-panel__head-icon">
                                    <v-icon small>mdi-paperclip</v-icon>
                                </span>
                                <div class="elib-panel__head-text">
                                    <div class="elib-panel__eyebrow">{{ $t('Files') }}</div>
                                    <h2 class="elib-panel__title">{{ selectedCategory.name || $t('Attachment') }}</h2>
                                </div>
                                <span class="elib-count">{{ result.length }}</span>
                            </div>

                            <v-data-table v-if="result.length" class="elib-table" :headers="headers" dense :items="result" :items-per-page="10">
                                <template v-slot:body>
                                    <draggable v-model="result" tag="tbody" handle=".drag-handle" @end="onDragEnd">
                                        <tr v-for="item in result" :key="item.id">
                                            <td>
                                                <v-icon class="drag-handle" small>mdi-drag</v-icon>
                                            </td>
                                            <td><v-icon :color="iconColors[item.extension]" v-text="icons[item.extension.toLowerCase()]" /></td>
                                            <td>{{ item.id }}</td>
                                            <td class="elib-cell-name">{{ item.fileName }}</td>
                                            <td>{{ item.title }}</td>
                                            <td class="elib-cell-size">{{ item.fileSize }}</td>
                                            <td v-if="selectedCategoryUploadToMaarifa">
                                                <v-chip
                                                    small
                                                    dark
                                                    class="maarifa-status-chip"
                                                    :color="item._reuploading ? 'grey' : (item.isUploadedToMaarifa ? 'green' : 'grey')"
                                                >
                                                    <v-progress-circular
                                                        v-if="item._reuploading"
                                                        indeterminate
                                                        color="white"
                                                        size="14"
                                                        width="2"
                                                    />
                                                    <span v-else>
                                                        {{ item.isUploadedToMaarifa ? $t('UploadedToMaarifa') : $t('NotUploadedToMaarifa') }}
                                                    </span>
                                                </v-chip>
                                            </td>
                                            <td class="text-center">
                                                <v-menu offset-y>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn icon x-small class="elib-icon-btn" :aria-label="$t('Options')" v-bind="attrs" v-on="on">
                                                            <v-icon small>mdi-dots-vertical</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <v-list dense>
                                                        <v-list-item @click="loadAttachment(item.recordId)">
                                                            <v-list-item-icon>
                                                                <v-icon color="teal">mdi-eye</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>{{ $t('View') }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item @click="setPermissions(item.id)">
                                                            <v-list-item-icon>
                                                                <v-icon color="warning">mdi-shield-account</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>{{ $t('Permissions') }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item
                                                            v-if="selectedCategoryUploadToMaarifa && !item.isUploadedToMaarifa"
                                                            :disabled="item._reuploading"
                                                            @click="reuploadToMaarifa(item)"
                                                        >
                                                            <v-list-item-icon>
                                                                <v-icon color="primary">mdi-cloud-upload</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>
                                                                {{ $t('ReuploadToMaarifa') }}
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item @click="deleteItem(item.id)">
                                                            <v-list-item-icon>
                                                                <v-icon color="error">mdi-delete</v-icon>
                                                            </v-list-item-icon>
                                                            <v-list-item-title>{{ $t('Delete') }}</v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>
                                            </td>
                                        </tr>
                                    </draggable>
                                </template>
                            </v-data-table>

                            <div v-else class="elib-empty">
                                <span class="elib-empty__icon">
                                    <v-icon large>mdi-file-document-outline</v-icon>
                                </span>
                                <p class="elib-empty__title">{{ $t('NoItemsToShow') }}</p>
                            </div>
                        </section>
                    </v-col>
                </v-row>
            </div>
        </v-card>

        <v-dialog v-if="addAttachmentDialog" v-model="addAttachmentDialog" width="600" persistent>
            <v-card>
                <v-toolbar dense dark color="primary" class="flex-grow-0">
                    <v-toolbar-title class="pa-0">
                        {{ $t('AddAttachment') }}
                    </v-toolbar-title>
                    <v-spacer />
                    <v-btn icon dark @click="clear">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-form ref="attachmentForm" v-model="valid" lazy-validation>
                        <v-container>
                            <v-row>
                                <v-col cols="5">
                                    <v-file-input v-model="attachmentFile" required :rules="nameRules" accept=".docx, .pdf, .png, .jpg, .xls, .xlsx, .mp4" :label="$t('ChooseAttachment')" :multiple="false" :disabled="uploadingFile" />
                                </v-col>
                                <v-col cols="7">
                                    <v-text-field v-model="attachmentName" :label="$t('Name')" maxlength="50" :disabled="uploadingFile" @keydown.enter.prevent="addAttachment" />
                                </v-col>

                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="secondary" tile class="white--text" small :disabled="uploadingFile" :loading="uploadingFile" @click="addAttachment">
                        {{ $t('Add') }}
                        <v-icon right>mdi-content-save</v-icon>
                    </v-btn>
                    <v-btn color="error" tile small :disabled="uploadingFile" @click="clear">
                        {{ $t('Cancel') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <CommonDialog v-model="attachmentDialog" :title="$t('Attachment')" css-content-class="pa-0" @input="$emit('input', false)">
            <v-container fluid class="stretch">
                <FileViewer :query="documentQuery" name="elibraryAttachment" />
            </v-container>
        </CommonDialog>

        <CommonDialog v-if="permissionDialog" v-model="permissionDialog" :title="$t('Permissions')" :fullscreen="false" width="820" css-content-class="pa-0" @input="$emit('input', false)">
            <GenericPermissions :item-id="selectedItem" :type-id="typeId" context-icon="mdi-file-document-outline" @close="permissionDialog = false" />
        </CommonDialog>
        <CommonDialog v-if="categoryPermissionDialog" v-model="categoryPermissionDialog" :title="$t('FolderPermissions')" :fullscreen="false" width="820" css-content-class="pa-0" @input="$emit('input', false)">
            <GenericPermissions :item-id="selectedCategoryId" :type-id="catTypeId" :context-name="selectedCategory.name" context-icon="mdi-folder-outline" @close="categoryPermissionDialog = false" />
        </CommonDialog>
        <v-form ref="elibraryCategoryForm" v-model="valid" lazy-validation>
            <CommonDialog v-model="dialog" :fullscreen="false" width="500" :title="title" show-action-toolbar>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="eLibraryCategory.nameAr" :label="$t('NameAr')" :rules="nameRules" required />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="eLibraryCategory.nameEn" :label="$t('NameEn')" :rules="nameRules" required />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field type="number" v-model="eLibraryCategory.orderIndex" :label="$t('Order')" :rules="nameRules" required />
                        </v-col>
                        <v-col cols="12">
                            <v-select v-model="eLibraryCategory.parentId" :loading="loading" outlined item-text="name" item-value="id" :items="categories" :label="$t('ParentCategory')" dense />
                        </v-col>
                        <v-col cols="12">
                            <v-switch v-model="eLibraryCategory.uploadToMaarifa"
                                      :label="$t('UploadToMaarifa')"
                                      inset
                                      color="primary" />
                        </v-col>
                    </v-row>
                </v-container>

                <template v-slot:actions>
                    <v-btn v-if="!edit" color="primary" class="white--text" tile :disabled="loading" small @click="save()">
                        {{ $t("Add") }}
                    </v-btn>
                    <v-btn v-if="edit" color="primary" class="white--text" tile :disabled="loading" small @click="update()">
                        {{ $t("Update") }}
                    </v-btn>
                    <v-btn color="error" class="white--text" tile :disabled="loading" small @click="dialog = false; reset()">
                        {{ $t("Cancel") }}
                    </v-btn>
                </template>
            </CommonDialog>
        </v-form>
    </v-container>
</template>

<script>
    import FileViewer from '@/components/controls/FileViewer.vue'
    import CommonDialog from '@/components/controls/CommonDialog.vue'
    import GenericPermissions from '@/components/app/admin/GenericPermissions.vue'
    import draggable from 'vuedraggable'
    import { PermissionTypesEnum } from '@/helpers/constants/enumerations'
    import LookupsService from '@/services/LookupsService.js'
    import ElibraryService from '@/services/ElibraryService.js'
    import ElibraryCategoryService from '@/services/ElibraryCategoryService.js'
    export default {
        components: {
            FileViewer, CommonDialog, GenericPermissions, draggable
        },

        data() {
            return {
                typeId: PermissionTypesEnum.Elibrary,
                catTypeId: PermissionTypesEnum.ElibraryCategory,
                valid: true,
                edit: false,
                search:'',
                filter: null,
                title: '',
                open:null,
                dialog:false,
                isActive: true,
                loading: false,
                attachmentDialog: false,
                permissionDialog: false,
                categoryPermissionDialog:false,
                uploadingFile: false,
                addAttachmentDialog: null,
                attachmentFile: null,
                attachmentName: null,
                attachmentCategoryId: null,
                selectedCategoryId: null,
                canBeDeleted: false,
                selectedCategory: {},
                documentQuery: null,
                selectedItem: null,
                attachments: [],
                categories: [],
                treecategories:[],
                result: [],
                selectedCategoryUploadToMaarifa: false,
                nameRules: [
                    v => !!v || this.$t('RequiredField')
                ],
                icons: {
                    '.pdf': 'mdi-file-pdf-box mdi-30px',
                    '.docx': 'mdi-file-word mdi-30px',
                    '.xlsx': 'mdi-file-excel mdi-30px',
                    '.file': 'mdi-file mdi-30px',
                    '.png': 'mdi-file-image mdi-30px',
                    '.jpg': 'mdi-file-image mdi-30px',
                },
                iconColors: {
                    '.pdf': 'red darken-3',
                    '.docx': 'blue darken-3',
                    '.xlsx': 'green darken-3',
                    '.file': 'blue-grey',
                    '.png': 'orange darken-4',
                    '.jpg': 'orange darken-4',
                },
                eLibraryCategory: {
                    nameAr: '',
                    nameEn: '',
                    orderIndex:null,
                    parentId:null,
                    uploadToMaarifa: false
                }
            }
        },

        created() {
            this.loadCategories()
        },

        computed: {
            headers() {
                const base = [
                    { text: '', value: 'drag' },
                    { text: '', value: 'icon' },
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('FileName'), value: 'fileName' },
                    { text: this.$t('Title'), value: 'title' },
                    { text: this.$t('FileSize'), value: 'fileSize' },
                ]

                const maarifaCol = this.selectedCategoryUploadToMaarifa
                    ? [{ text: this.$t('MaarifaStatus'), value: 'maarifaStatus' }]
                    : []

                return [
                    ...base,
                    ...maarifaCol,
                    { text: this.$t('Options'), value: 'options', width: '12%' }
                ]
            }
        },

        methods: {
            // translate with a built-in fallback, so labels still read correctly when
            // the key has not been added to the translations table yet
            tr(key, fallbackEn, fallbackAr) {
                if (this.$te(key)) {
                    return this.$t(key)
                }
                return this.$i18n.locale === 'ar' ? fallbackAr : fallbackEn
            },

            findCategoryById(categories, id) {
                if (!categories || !id) {
                    return null
                }

                for (const category of categories) {
                    if (category.id === id) {
                        return category
                    }

                    if (category.children && category.children.length) {
                        const found = this.findCategoryById(category.children, id)
                        if (found) {
                            return found
                        }
                    }
                }

                return null
            },
            onDragEnd(evt) {
                this.updateTableOrder()
            },
            async updateTableOrder() {
                ElibraryService.updateOrder(this.result)
                /*try {
                    // Make the server call here to save the updated order
                    const response = await fetch('/your-api-endpoint', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(this.result),
                    });

                    if (!response.ok) {
                        throw new Error('Failed to update the order');
                    }

                    // Optionally handle response
                    console.log('Order updated successfully');
                } catch (error) {
                    console.error(error);
                }*/
            },
            addNewCategory() {
                this.dialog = true
                this.eLibraryCategory.parentId = this.selectedCategoryId
            },
            editCategory() {
                ElibraryCategoryService.getElibraryCategory(this.selectedCategoryId).then((res) => {
                    if (res.data) {
                        var selectedCategory = res.data
                        if (selectedCategory) {
                            this.eLibraryCategory = {
                                nameAr: selectedCategory.nameAr,
                                nameEn: selectedCategory.nameEn,
                                orderIndex: selectedCategory.orderIndex,
                                parentId: selectedCategory.parentId,
                                uploadToMaarifa: selectedCategory.uploadToMaarifa === true
                            }
                            this.edit = true
                            this.dialog = true
                        }
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
                
            },
            handleSearch(input) {
                if (input) {
                    this.$refs.tree.updateAll(true)
                } else {
                    this.$refs.tree.updateAll(false)
                }
            },
            clear() {
                this.addAttachmentDialog = false
                this.attachmentFile = null
                this.attachmentName = null
                this.attachmentTypeId = null
            },

            updateSelectedCategoryUploadFlag() {
                if (!this.selectedCategoryId) {
                    this.selectedCategoryUploadToMaarifa = false
                    return
                }

                ElibraryCategoryService.getElibraryCategory(this.selectedCategoryId).then((res) => {
                    if (res.data) {
                        this.selectedCategoryUploadToMaarifa = res.data.uploadToMaarifa === true
                    } else {
                        this.selectedCategoryUploadToMaarifa = false
                    }
                }).catch(() => {
                    this.selectedCategoryUploadToMaarifa = false
                })
            },

            loadCategories() {
                LookupsService.listElibraryCategories().then((res) => {
                    if (res.data) {
                        this.categories = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
                ElibraryCategoryService.listElibraryCategories().then((res) => {
                    if (res.data) {
                        this.treecategories = res.data

                        const currentSelectedId = this.selectedCategoryId
                        let selectedCategory = null

                        if (currentSelectedId) {
                            selectedCategory = this.findCategoryById(res.data, currentSelectedId)
                        }

                        if (!selectedCategory && res.data.length > 0) {
                            selectedCategory = res.data[0]
                        }

                        if (selectedCategory) {
                            this.selectedCategoryId = selectedCategory.id
                            this.selectedCategory = selectedCategory
                            this.canBeDeleted = selectedCategory.parentId != null && selectedCategory.parentId != undefined
                            this.eLibraryCategory.parentId = this.selectedCategoryId
                            this.loadCategory(this.selectedCategoryId)
                        }
                        this.updateSelectedCategoryUploadFlag()
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            addAttachment() {
                if (this.$refs.attachmentForm.validate()) {
                    let formdata = new FormData()
                    formdata.append('attachment', this.attachmentFile)
                    formdata.append('filename', this.attachmentName == null ? '' : this.attachmentName)
                    formdata.append('elibraryCategoryId', this.selectedCategoryId)
                    this.uploadingFile = true
                    ElibraryService.addAttachment(formdata).then((res) => {
                        if (res.success) {
                            this.loadCategory(this.selectedCategoryId)
                            this.$root.$notification(this.$t('FileSavedSuccess'), 'success')
                        }
                        this.clear()
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.uploadingFile = false
                    })
                }
            },

            loadCategory(id) {
                this.loading = true
                ElibraryService.loadCategory(id).then((res) => {
                    if (res.data) {
                        this.result = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            loadAttachment(recordId) {
                ElibraryService.getAttachment(recordId).then(response => {
                    this.documentQuery = response.data
                    this.attachmentDialog = true
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                })
            },

            show(category) {
                this.selectedCategoryId = category.id
                this.canBeDeleted = this.treecategories[0].id != category.id
                this.selectedCategory = category
                this.eLibraryCategory.parentId = this.selectedCategoryId
                this.updateSelectedCategoryUploadFlag()
                this.loadCategory(category.id)
            },

            async deleteItem(attachmentId) {
                if (await this.$root.$confirm(this.$t('Delete'), this.$t('ConfirmDelete'))) {
                    this.loading = true
                    ElibraryService.deleteElibrary(attachmentId)
                        .then(() => {
                            this.loadCategory(this.selectedCategoryId)
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },
            async deleteCategory(categoryId) {
                if (await this.$root.$confirm(this.$t('Delete'), this.$t('ConfirmDelete') + ': ' + this.selectedCategory.name)) {
                    this.loading = true
                    ElibraryCategoryService.deleteElibraryCategory(categoryId)
                        .then(() => {
                            this.loadCategories()
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },
            setPermissions(itemId) {
                this.selectedItem = itemId
                this.permissionDialog = true
            },
            reuploadToMaarifa(item) {
                this.$set(item, '_reuploading', true)
                ElibraryService.reuploadToMaarifa(item.id)
                    .then((res) => {
                        if (res.success) {
                            this.$root.$notification(this.$t('UploadedToMaarifaSuccess'), 'success')
                            this.loadCategory(this.selectedCategoryId)
                        } else {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.$set(item, '_reuploading', false)
                    })
            },
            save() {
                if (this.$refs.elibraryCategoryForm.validate()) {
                    ElibraryCategoryService.addElibraryCategory(this.eLibraryCategory).then((res) => {
                        this.loadCategories()
                        this.reset()
                        this.dialog = false
                        this.$refs.elibraryCategoryForm.resetValidation()
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            update() {
                if (this.$refs.elibraryCategoryForm.validate()) {
                    ElibraryCategoryService.updateElibraryCategory(this.selectedCategoryId, this.eLibraryCategory).then((res) => {
                        this.dialog = false
                        this.loadCategories()
                        this.reset()
                        this.edit = false
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },
            reset() {
                this.eLibraryCategory.nameAr = ''
                this.eLibraryCategory.nameEn = ''
                this.eLibraryCategory.orderIndex = null
                this.eLibraryCategory.parentId = null
            },
        }
    }
</script>

<style>
    .e-libaray .active h3 { color: #fff !important }

    .e-libaray .active i { color: #fff !important }

    .stretch { height: 100% }

    .maarifa-status-chip {
        min-width: 36px;
        justify-content: center;
    }

    .maarifa-status-chip .v-progress-circular {
        margin: 0;
    }

    .e-library-tree-panel {
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - 300px);
    }

    .e-library-tree-search {
        flex-shrink: 0;
    }

    .e-library-tree-scroll {
        flex: 1;
        min-height: 220px;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 10px 12px 16px;
    }

    /* ==========================================================================
       Electronic library — folder workspace
       Namespaced under .elib-page so nothing leaks into other pages.
       ========================================================================== */

    .elib-page {
        --elib-surface: #ffffff;
        --elib-border: #e4e9ee;
        --elib-line: #d7dfe5;
        --elib-text: #2b3a42;
        --elib-muted: #78899a;
        --elib-radius: 14px;
        /* Muted teal-green accent, the same value the organizational-structure page uses
           for --org-accent. It replaces --v-primary-lighten5, which Vuetify derives from
           the theme primary and which resolved to a cyan (#a4f9f5) here. */
        --elib-tint: #84a6a5;
        max-width: 1680px;
        margin: 0 auto;
    }

    .elib-page .elib-shell {
        border-radius: var(--elib-radius);
        border: 1px solid var(--elib-border);
        box-shadow: 0 1px 2px rgba(16, 36, 44, .04), 0 6px 20px rgba(16, 36, 44, .06);
        background: var(--elib-surface);
        overflow: hidden;
    }

    /* ---------- toolbar ---------- */
    .elib-page .elib-toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        padding: 14px 16px;
        border-bottom: 1px solid var(--elib-border);
        background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
    }

    .elib-page .elib-toolbar__divider {
        width: 1px;
        height: 24px;
        background: var(--elib-line);
    }

    .elib-page .elib-btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-size: 14px !important;
        font-weight: 600;
        min-width: 0;
        padding: 0 14px;
    }

    .elib-page .elib-btn--muted {
        background: #eef1f4 !important;
        color: var(--elib-text) !important;
    }

    /* icon-only buttons fill with colour on hover/focus, because the global
       stylesheet forces icons inside a hovered/focused button to white */
    .elib-page .elib-icon-btn {
        border-radius: 9px;
        background: #eff3f6;
        transition: background .18s ease;
    }

    .elib-page .elib-icon-btn .v-icon {
        color: var(--elib-muted);
    }

    .elib-page .elib-icon-btn:hover,
    .elib-page .elib-icon-btn:focus {
        background: var(--v-primary-base);
    }

    /* ---------- panels ---------- */
    .elib-page .elib-panel {
        background: var(--elib-surface);
        border: 1px solid var(--elib-border);
        border-radius: var(--elib-radius);
        overflow: hidden;
    }

    .elib-page .elib-panel__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--elib-border);
        background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
        flex-shrink: 0;
    }

    .elib-page .elib-panel__head-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        flex: 0 0 30px;
        border-radius: 9px;
        background: var(--elib-tint);
    }

    .elib-page .elib-panel__head-icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .elib-page .elib-panel__head-text {
        min-width: 0;
    }

    .elib-page .elib-panel__eyebrow {
        font-size: 11px;
        letter-spacing: .05em;
        text-transform: uppercase;
        color: var(--elib-muted);
        line-height: 1.4;
    }

    .elib-page .elib-panel__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--elib-text);
        line-height: 1.4;
        margin: 0;
        word-break: break-word;
    }

    .elib-page .elib-panel__toolbar {
        padding: 14px 16px;
        border-bottom: 1px solid var(--elib-border);
    }

    .elib-page .elib-search .v-input__slot {
        background: #f7f9fa !important;
    }

    .elib-page .elib-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 26px;
        height: 22px;
        padding: 0 8px;
        border-radius: 999px;
        background: var(--elib-tint);
        color: var(--v-primary-base);
        font-size: 12px;
        font-weight: 600;
    }

    /* ---------- folder tree ---------- */
    .elib-page .elib-tree .v-treeview-node__root {
        min-height: 42px;
        padding: 0;
    }

    .elib-page .elib-tree .v-treeview-node__content {
        margin: 0;
    }

    .elib-page .elib-tree .v-treeview-node__label {
        flex: 1 1 auto;
        overflow: visible;
        font-size: 14px;
    }

    .elib-page .elib-tree .v-treeview-node__toggle {
        color: var(--elib-muted);
    }

    /* `activatable` makes Vuetify paint its own overlay on ::before of the node ROOT, which spans the
       whole row including the expand toggle:
           .v-treeview-node__root.v-treeview-node--active::before { opacity: .12 }   (also :hover/:focus)
       .elib-node already carries the selected state and covers only the content area, so that overlay
       showed as a stray highlight beside the toggle. Drop it and keep selection on the item itself.
       !important because Vuetify's selectors are more specific than this one. */
    .elib-page .elib-tree .v-treeview-node__root::before {
        display: none !important;
    }

    .elib-page .elib-tree .v-treeview-node__children {
        position: relative;
    }

    /* hierarchy guide lines: 24px level spacers, so level 1 sits at 12px */
    .elib-page .elib-tree .v-treeview-node__children::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 14px;
        width: 1px;
        background: var(--elib-line);
        pointer-events: none;
    }

    .v-application--is-ltr .elib-page .elib-tree .v-treeview-node__children::before {
        left: 12px;
    }

    .v-application--is-ltr .elib-page .elib-tree .v-treeview-node__children .v-treeview-node__children::before {
        left: 36px;
    }

    .v-application--is-ltr .elib-page .elib-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        left: 60px;
    }

    .v-application--is-rtl .elib-page .elib-tree .v-treeview-node__children::before {
        right: 12px;
    }

    .v-application--is-rtl .elib-page .elib-tree .v-treeview-node__children .v-treeview-node__children::before {
        right: 36px;
    }

    .v-application--is-rtl .elib-page .elib-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        right: 60px;
    }

    .elib-page .elib-node {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 7px 10px;
        border-radius: 10px;
        cursor: pointer;
        transition: background .18s ease, box-shadow .18s ease;
    }

    .elib-page .elib-node:hover {
        background: var(--elib-tint);
    }

    .elib-page .elib-node:focus {
        outline: none;
        background: var(--elib-tint);
        box-shadow: 0 0 0 2px var(--v-primary-base);
    }

    .elib-page .elib-node--selected,
    .elib-page .elib-node--selected:hover {
        background: var(--elib-tint);
        box-shadow: inset 0 0 0 1px var(--v-primary-base);
    }

    .elib-page .elib-node__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        border-radius: 9px;
        background: #eff3f6;
    }

    .elib-page .elib-node__icon .v-icon {
        font-size: 16px;
        color: var(--elib-muted) !important;
    }

    .elib-page .elib-node--branch .elib-node__icon {
        background: var(--elib-tint);
    }

    .elib-page .elib-node--branch .elib-node__icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .elib-page .elib-node--selected .elib-node__icon {
        background: var(--v-primary-base);
    }

    .elib-page .elib-node--selected .elib-node__icon .v-icon {
        color: #fff !important;
    }

    .elib-page .elib-node__text {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--elib-text);
        line-height: 1.5;
    }

    .elib-page .elib-node--selected .elib-node__text {
        color: var(--v-primary-base);
        font-weight: 600;
    }

    /* ---------- attachments table ---------- */
    .elib-page .elib-table thead th {
        background: #fbfcfd;
        white-space: nowrap;
        border-bottom: 1px solid var(--elib-border) !important;
    }

    .elib-page .elib-table tbody tr {
        transition: background .15s ease;
    }

    .elib-page .elib-table tbody tr:hover {
        background: var(--elib-tint) !important;
    }

    .elib-page .elib-table tbody td {
        border-bottom: 1px solid #f0f3f5 !important;
    }

    .elib-page .elib-cell-name {
        max-width: 260px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .elib-page .elib-cell-size {
        white-space: nowrap;
        color: var(--elib-muted);
    }

    .elib-page .drag-handle {
        cursor: move;
    }

    /* ---------- empty state ---------- */
    .elib-page .elib-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 48px 24px;
    }

    .elib-page .elib-empty__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 68px;
        height: 68px;
        border-radius: 50%;
        background: var(--elib-tint);
        margin-bottom: 14px;
    }

    .elib-page .elib-empty__icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .elib-page .elib-empty__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--elib-text);
        margin: 0;
    }

    /* ---------- responsive ---------- */
    @media (max-width: 959px) {
        .e-library-tree-panel {
            max-height: 380px;
        }

        .elib-page .elib-toolbar {
            padding: 12px;
        }

        .elib-page .elib-toolbar__divider {
            display: none;
        }
    }

    @media (max-width: 599px) {
        .elib-page .elib-toolbar .elib-btn {
            flex: 1 1 100%;
        }

        .elib-page .elib-cell-name {
            max-width: 140px;
        }
    }
</style>